/**
 * MeasureWidget — routes a dashboard widget to the appropriate renderer.
 *
 * Performance-fact measures (office_performance, competition_health, etc.)
 * use the GenericMetricWidget which supports dynamic group_by, chart type
 * switching, and drill-down.
 *
 * Separate-table measures (speed_to_contract, contract_duration, etc.)
 * use their existing bespoke section components since they rely on
 * percentile data not available in the performance_fact table.
 */
import { useMemo, type ComponentType } from 'react'
import { useExecDashboard } from '../../contexts/ExecDashboardContext'
import type { DashboardWidget } from '../../types/exec'
import type { DashboardFilters } from '../../types/govdata'

// Generic widget for performance-fact measures
import GenericMetricWidget from './generic/GenericMetricWidget'

// Legacy section components for separate-table measures
import SpeedToContractSection from '../govdata/SpeedToContractSection'
import FyEndSurgeSection from '../govdata/FyEndSurgeSection'
import ContractDurationSection from '../govdata/ContractDurationSection'
import VendorConcentrationSection from '../govdata/VendorConcentrationSection'

/**
 * Measures backed by the universal performance_fact table.
 * These all use GenericMetricWidget.
 */
const PERFORMANCE_FACT_MEASURES = new Set([
  'office_performance',
  'competition_health',
  'small_biz_engagement',
  'contract_type_trends',
  'ot_prevalence',
  'acq_performance',
  'activity_by_vertical',
  'geo_performance',
])

/**
 * Legacy components for measures backed by separate tables. They accept an
 * `embedded` flag so the inner SectionCard fills the widget tile (flex-col,
 * chart in a flex-1 child) instead of using its standalone-page heights.
 */
const FILTER_COMPONENTS: Record<string, ComponentType<{ filters: DashboardFilters; embedded?: boolean }>> = {
  speed_to_contract: SpeedToContractSection,
  fy_end_surge: FyEndSurgeSection,
  contract_duration: ContractDurationSection,
}

const FISCAL_YEAR_COMPONENTS: Record<string, ComponentType<{ fiscalYear?: number; embedded?: boolean }>> = {
  vendor_concentration: VendorConcentrationSection,
}

interface Props {
  widget: DashboardWidget
}

export default function MeasureWidget({ widget }: Props) {
  const { filters: dashboardFilters } = useExecDashboard()

  // Merge dashboard-level filters with widget-specific config overrides
  const mergedFilters: DashboardFilters = useMemo(() => ({
    ...dashboardFilters,
    ...(widget.config.fiscal_year ? { fiscal_year: widget.config.fiscal_year as number } : {}),
    ...(widget.config.sub_agency_code ? { sub_agency_code: widget.config.sub_agency_code as string } : {}),
    ...(widget.config.office_code ? { office_code: widget.config.office_code as string } : {}),
    // speed_to_contract: pick the FAR vs Other Transaction population
    ...(widget.config.is_ot !== undefined ? { is_ot: widget.config.is_ot as boolean } : {}),
  }), [dashboardFilters, widget.config])

  const fiscalYear = mergedFilters.fiscal_year

  // Performance-fact measures → GenericMetricWidget
  if (PERFORMANCE_FACT_MEASURES.has(widget.measure)) {
    return <GenericMetricWidget widget={widget} />
  }

  // Legacy filter-based components — embedded mode so the SectionCard
  // chrome fills the widget tile rather than imposing standalone heights.
  const FilterComponent = FILTER_COMPONENTS[widget.measure]
  if (FilterComponent) {
    return <FilterComponent filters={mergedFilters} embedded />
  }

  // Fiscal-year-only components (need a single number, take the latest from range)
  const FYComponent = FISCAL_YEAR_COMPONENTS[widget.measure]
  if (FYComponent) {
    const singleFY = typeof fiscalYear === 'string'
      ? Math.max(...fiscalYear.split(',').map(Number).filter(Boolean))
      : typeof fiscalYear === 'number' ? fiscalYear : undefined
    return <FYComponent fiscalYear={singleFY || undefined} embedded />
  }

  // Unknown measure
  return (
    <div className="flex items-center justify-center h-full text-sm text-gray-400">
      Unknown measure: {widget.measure}
    </div>
  )
}
